import type { SpotlightMyApp, SpotlightMyAppsData } from '../types';

export type NavigateMode = 'same-tab' | 'new-tab';

export interface ResolvedNavigateTarget {
  href: string;
  mode: NavigateMode;
  applicationKey?: string;
}

function normalizeKey(value: string | undefined | null): string {
  return (value ?? '').trim().toLowerCase();
}

/** Hostname dari URL absolut; string kosong jika invalid. */
export function hostnameOf(url: string | undefined | null): string {
  if (!url?.trim()) return '';
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

/** Origin (scheme + host + port) dari app_url; kosong jika invalid. */
export function originOf(url: string | undefined | null): string {
  if (!url?.trim()) return '';
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

/**
 * Infer app yang sedang dibuka: hostname halaman ∈ my-app.app_url.
 * Cocokkan case-insensitive; tidak perlu init({ applicationKey }).
 */
export function findAppByHostname(
  apps: SpotlightMyAppsData | null | undefined,
  hostname: string,
): SpotlightMyApp | undefined {
  const host = hostname.trim().toLowerCase();
  if (!host || !apps?.length) return undefined;
  return apps.find((app) => hostnameOf(app.app_url) === host);
}

export function findAppByKey(
  apps: SpotlightMyAppsData | null | undefined,
  applicationKey: string | undefined | null,
): SpotlightMyApp | undefined {
  const key = normalizeKey(applicationKey);
  if (!key || !apps?.length) return undefined;
  return apps.find((app) => normalizeKey(app.app_initial_name) === key);
}

/**
 * Gabung base app_url + path catalog.
 * Path catalog diasumsikan root-relative ke origin app (`/planning/ppic`).
 * Absolute http(s) dikembalikan apa adanya.
 */
export function joinAppPath(appUrl: string, path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return appUrl;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const origin = originOf(appUrl);
  if (!origin) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

  const rel = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return new URL(rel, `${origin}/`).href;
}

/**
 * Resolve navigasi hasil catalog / rekomendasi.
 *
 * - Same app (SPA BPM/HO): same-tab ke path relatif / URL same-origin
 * - Lintas app: new-tab ke absolute URL dari my-app.app_url + path
 * - current app di-infer dari `currentHostname` ∩ my-app
 */
export function resolveCatalogNavigate(options: {
  apps: SpotlightMyAppsData | null | undefined;
  currentHostname: string;
  applicationKey: string | undefined | null;
  path: string | undefined | null;
}): ResolvedNavigateTarget | null {
  const path = options.path?.trim();
  if (!path) return null;

  const currentApp = findAppByHostname(options.apps, options.currentHostname);
  const currentKey = normalizeKey(currentApp?.app_initial_name);
  const targetKey = normalizeKey(options.applicationKey);
  const targetApp = findAppByKey(options.apps, options.applicationKey);

  // Absolute URL di catalog — bandingkan host langsung.
  if (/^https?:\/\//i.test(path)) {
    const pathHost = hostnameOf(path);
    const sameHost = pathHost !== '' && pathHost === options.currentHostname.toLowerCase();
    return {
      href: path,
      mode: sameHost ? 'same-tab' : 'new-tab',
      applicationKey: targetKey || undefined,
    };
  }

  const sameApp =
    Boolean(currentKey) && Boolean(targetKey) && currentKey === targetKey;

  if (sameApp) {
    return {
      href: path.startsWith('/') ? path : `/${path}`,
      mode: 'same-tab',
      applicationKey: targetKey,
    };
  }

  // Host tidak ketemu di my-app, tapi target key kosong / sama-ish → treat as in-app path.
  if (!targetKey || (!targetApp && !currentKey)) {
    return {
      href: path.startsWith('/') ? path : `/${path}`,
      mode: 'same-tab',
    };
  }

  // Lintas app tanpa entry my-app (tidak punya akses / key mismatch) — jangan tebak domain.
  if (!targetApp) {
    return null;
  }

  const href = joinAppPath(targetApp.app_url, path);
  const sameHost = hostnameOf(href) === options.currentHostname.toLowerCase();

  return {
    href,
    mode: sameHost ? 'same-tab' : 'new-tab',
    applicationKey: targetKey,
  };
}

/** Buka app dari daftar idle "Aplikasi". */
export function resolveAppNavigate(options: {
  app: SpotlightMyApp | undefined;
  currentHostname: string;
}): ResolvedNavigateTarget | null {
  if (!options.app?.app_url) return null;

  const sameHost = hostnameOf(options.app.app_url) === options.currentHostname.toLowerCase();
  return {
    href: options.app.app_url,
    mode: sameHost ? 'same-tab' : 'new-tab',
    applicationKey: normalizeKey(options.app.app_initial_name) || undefined,
  };
}

export function applyNavigate(target: ResolvedNavigateTarget): void {
  if (target.mode === 'same-tab') {
    window.location.assign(target.href);
    return;
  }
  window.open(target.href, '_blank', 'noopener,noreferrer');
}
